import { useSearchParams } from 'react-router-dom'
import { BlogSidebar } from '@/components/website/BlogSidebar'
import { BlogPostList } from '@/components/website/BlogPostList'
import { PageBreadcrumb } from '@/components/ui/PageBreadcrumb'
import { useWebsiteBlogMeta, useWebsiteBlogs } from '@/services/httpServices/queries/useWebsite'
import { flattenBlogTree } from '@/data/blog'

function parseCsv(value: string | null): string[] {
  return (value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

export function Blog() {
  const [searchParams] = useSearchParams()
  const search = searchParams.get('search')?.trim() || undefined
  const categorySlugs = parseCsv(searchParams.get('categories'))
  const tagSlugs = parseCsv(searchParams.get('tags'))

  const { posts, isLoading } = useWebsiteBlogs({ categories: categorySlugs, tags: tagSlugs, search })
  const { categories, tagTree } = useWebsiteBlogMeta()

  const categoryNames = flattenBlogTree(categories)
    .filter((node) => categorySlugs.includes(node.slug))
    .map((node) => node.name)
  const tagNames = flattenBlogTree(tagTree)
    .filter((node) => tagSlugs.includes(node.slug))
    .map((node) => node.name)
  const activeFilters = [...categoryNames, ...tagNames]

  const heading = search
    ? `Search results for “${search}”`
    : activeFilters.length
      ? `Filtered by: ${activeFilters.join(', ')}`
      : undefined

  return (
    <>
      <PageBreadcrumb title="Our Blog" />

      <section className="blog-section pt-100 md-pt-80 pb-100 md-pb-80">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              {heading && (
                <div className="mb-4">
                  <h2 className="text-2xl font-semibold">{heading}</h2>
                  {activeFilters.length > 0 && !search && (
                    <p className="text-muted mb-0">{posts.length} post(s) found</p>
                  )}
                </div>
              )}

              {isLoading && !posts.length ? (
                <div className="flex min-h-[30vh] items-center justify-center">
                  <i className="fa-solid fa-circle-notch fa-spin text-3xl text-primary" aria-label="Loading" />
                </div>
              ) : (
                <BlogPostList
                  posts={posts}
                  emptyMessage={
                    search
                      ? `No posts found for “${search}”.`
                      : activeFilters.length
                        ? 'No posts match the selected filters.'
                        : 'No blog posts found.'
                  }
                />
              )}
            </div>

            <div className="col-lg-4">
              <BlogSidebar />
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
